// Full-document render of the tempurpedic FlagshipIndex page: PageSkeleton
// (the React document shell) wrapping the body sections in order (normal
// site chrome):
//
// 1. `.quicksearch` -- raw title bar + headline (server-gettext; the retailer
//    heading's msgid embeds a literal `&reg;` entity, injected raw to keep
//    rendering as the (R) character) around the shared LocatorQuickSearchSSR
//    component.
// 2. `.locations` -- the shared FlagshipNearestStoresSSR component
//    (`max_tiles=4 include_location_info=True`) plus the hardcoded
//    see-all-stores button (a literal URL, not a page url; the copy is
//    intentionally untranslated).
// 3. `.pullquote` (when `featured_quote` is set) -- the curly-quoted quote +
//    em-dash author, and the FlagshipReviewsLink scroll button when reviews
//    exist.
// 4. The slideshow section (always rendered) -- the FlagshipSlideshow
//    component fed by the `slides_data` JSON-string contract.
// 5. `.callouts` (when media_object rows exist) -- raw DOM over the
//    FlagshipIndexMediaObject Orderable rows; `copy` is injected raw
//    (rich-text DB form, never expanded).
// 6. The carousel section (when carousel rows exist) -- FlagshipCarousel fed
//    by the `carousel_data` JSON-string contract.
// 7. `.social` (when social_networks rows exist) -- per-network anchors with
//    1792-viewBox SVG sprites; an unknown network name renders an EMPTY
//    anchor (no fallback icon).
// 8. The inline reviews section (FlagshipIndexReviews: star-rating matrix +
//    the counter==3 read-more splice as component state).
//
// (Section classes are anchored in flagship-store.scss /
// retail-locator-modules.scss.)
import React from "react";
import { jt, t } from "ttag";

import { templates } from "@reactivated";

import {
    FlagshipCarousel,
    ICarouselCell,
} from "../apps/retail-locator/FlagshipCarousel";
import { FlagshipIndexReviews } from "../apps/retail-locator/FlagshipIndexReviews";
import { FlagshipNearestStoresSSR } from "../apps/retail-locator/FlagshipNearestStoresSSR";
import { FlagshipReviewsLink } from "../apps/retail-locator/FlagshipReviewsLink";
import {
    FlagshipSlideshow,
    ISlideshowCell,
} from "../apps/retail-locator/FlagshipSlideshow";
import { LocatorQuickSearchSSR } from "../apps/retail-locator/LocatorQuickSearchSSR";
import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.FlagshipIndexTemplate;

// The five social-icon sprite paths (facebook-f, pinterest-p, twitter,
// instagram, youtube-play), keyed by the SocialNetworks `name` choice value.
const SOCIAL_ICON_PATHS: Record<string, string> = {
    facebook:
        "M1343 12v264h-157q-86 0-116 36t-30 108v189h293l-39 296h-254v759H734V905H479V609h255V391q0-186 104-288.5T1115 0q147 0 228 12",
    pinterest:
        "M256 597q0-108 37.5-203.5T397 227t152-123 185-78T936 0q158 0 294 66.5T1451 260t85 287q0 96-19 188t-60 177-100 149.5-145 103-189 38.5q-68 0-135-32t-96-88q-10 39-28 112.5t-23.5 95-20.5 71-26 71-32 62.5-46 77.5-62 86.5l-14 5-9-10q-15-157-15-188 0-92 21.5-206.5T604 972t52-203q-32-65-32-169 0-83 52-156t132-73q61 0 95 40.5T937 514q0 66-44 191t-44 187q0 63 45 104.5t109 41.5q55 0 102-25t78.5-68 56-95 38-110.5 20-111 6.5-99.5q0-173-109.5-269.5T909 163q-200 0-334 129.5T441 621q0 44 12.5 85t27 65 27 45.5T520 847q0 28-15 73t-37 45q-2 0-17-3-51-15-90.5-56t-61-94.5-32.5-108T256 597",
    twitter:
        "M1684 408q-67 98-162 167 1 14 1 42 0 130-38 259.5T1369.5 1125 1185 1335.5t-258 146-323 54.5q-271 0-496-145 35 4 78 4 225 0 401-138-105-2-188-64.5T285 1033q33 5 61 5 43 0 85-11-112-23-185.5-111.5T172 710v-4q68 38 146 41-66-44-105-115t-39-154q0-88 44-163 121 149 294.5 238.5T884 653q-8-38-8-74 0-134 94.5-228.5T1199 256q140 0 236 102 109-21 205-78-37 115-142 178 93-10 186-50",
    instagram:
        "M1490 1426V778h-135q20 63 20 131 0 126-64 232.5T1137 1310t-240 62q-197 0-337-135.5T420 909q0-68 20-131H299v648q0 26 17.5 43.5T360 1487h1069q25 0 43-17.5t18-43.5m-284-533q0-124-90.5-211.5T897 594q-127 0-217.5 87.5T589 893t90.5 211.5T897 1192q128 0 218.5-87.5T1206 893m284-360V368q0-28-20-48.5t-49-20.5h-174q-29 0-49 20.5t-20 48.5v165q0 29 20 49t49 20h174q29 0 49-20t20-49m174-208v1142q0 81-58 139t-139 58H325q-81 0-139-58t-58-139V325q0-81 58-139t139-58h1142q81 0 139 58t58 139",
    youtube:
        "M1280 896q0-37-30-54L738 522q-31-20-65-2-33 18-33 56v640q0 38 33 56 16 8 31 8 20 0 34-10l512-320q30-17 30-54m512 0q0 96-1 150t-8.5 136.5T1760 1330q-16 73-69 123t-124 58q-222 25-671 25t-671-25q-71-8-124.5-58T31 1330q-14-65-21.5-147.5T1 1046 0 896t1-150 8.5-136.5T32 462q16-73 69-123t124-58q222-25 671-25t671 25q71 8 124.5 58t69.5 123q14 65 21.5 147.5T1791 746t1 150",
};

const SocialIcon = (props: { name: string }): React.JSX.Element | null => {
    const path = SOCIAL_ICON_PATHS[props.name];
    if (!path) {
        return null;
    }
    return (
        <svg
            className="social__icon"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            width="1792"
            height="1792"
            viewBox="0 0 1792 1792"
        >
            <path d={path} />
        </svg>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const slides = JSON.parse(props.slides_data) as ISlideshowCell[];
    const carouselCells = JSON.parse(props.carousel_data) as ICarouselCell[];
    // jt preserves the headline's inline <sup>, and its msgid matches the one
    // FlagshipMediaObjectBlock already extracts (shared fr_CA entry).
    const reg = <sup key="reg">&reg;</sup>;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="quicksearch">
                <div className="quicksearch__title-bar">
                    <h1
                        className="quicksearch__title"
                        dangerouslySetInnerHTML={{
                            __html: props.find_retailer_heading,
                        }}
                    />
                </div>
                <h2 className="quicksearch__headline">
                    {props.quick_search_heading}
                </h2>
                <section className="l-capped-width">
                    <LocatorQuickSearchSSR
                        selected_item="flagship"
                        hide_elite={props.page.hide_elite}
                        hide_tp_stores={props.page.hide_tp_stores}
                    />
                </section>
            </div>

            <div className="locations">
                <div className="locations__container">
                    <FlagshipNearestStoresSSR
                        max_tiles={4}
                        include_location_info={true}
                    />
                </div>
                <div className="locations__button">
                    <a href="/all-tempur-pedic-stores" className="button">
                        See All Tempur-Pedic<sup>®</sup> Stores
                    </a>
                </div>
            </div>

            {props.page.featured_quote ? (
                <section className="pullquote">
                    <div className="pullquote__quote">
                        <p className="pullquote__copy">
                            {"“"}
                            {props.page.featured_quote}
                            {"”"}{" "}
                            <span className="pullquote__copy--deemphasize">
                                {"— "}
                                {props.page.featured_quote_author}
                            </span>
                        </p>
                    </div>
                    {props.reviews.length > 0 ? <FlagshipReviewsLink /> : null}
                </section>
            ) : null}

            <section role="region" aria-label={props.slideshow_aria_label}>
                <FlagshipSlideshow images={slides} />
            </section>

            {props.media_objects.length > 0 ? (
                <section className="callouts l-capped-width">
                    <div className="lead-in">
                        <h3 className="lead-in__headline">
                            {jt`Why visit a Tempur-Pedic${reg} store?`}
                        </h3>
                    </div>
                    {props.media_objects.map((item, i) => (
                        <div key={i} className="callouts__item">
                            <h5 className="callouts__headline">
                                {item.headline}
                            </h5>
                            <div
                                className="callouts__descript"
                                dangerouslySetInnerHTML={{ __html: item.copy }}
                            />
                        </div>
                    ))}
                </section>
            ) : null}

            {carouselCells.length > 0 ? (
                <section role="region" aria-label={props.carousel_aria_label}>
                    <FlagshipCarousel images={carouselCells} />
                </section>
            ) : null}

            {props.social_networks.length > 0 ? (
                <div className="social">
                    <div className="social__headline">
                        <h1>{t`Stay Updated — Connect With Us!`}</h1>
                    </div>
                    <div className="social__services">
                        {props.social_networks.map((item, i) => (
                            <a
                                key={i}
                                className="social__service"
                                href={item.url}
                                title={item.name}
                            >
                                <SocialIcon name={item.name} />
                            </a>
                        ))}
                    </div>
                </div>
            ) : null}

            <FlagshipIndexReviews
                reviews={props.reviews}
                pageTitle={props.page.title}
            />
        </PageSkeleton>
    );
};
